import React, { FC, useEffect, useState } from 'react'

import {
  BooleanInput,
  BulkDeleteButton,
  BulkUpdateButton,
  Button,
  Datagrid,
  DateField,
  DeleteButton,
  FilterButton,
  List,
  Pagination,
  ReferenceManyField,
  TopToolbar,
  downloadCSV,
  useDataProvider,
  useRecordContext,
  useListContext
} from 'react-admin'
import { Steps, Tag, Typography } from 'antd'

import GetAppIcon from '@mui/icons-material/GetApp'
import jsonExport from 'jsonexport/dist'

import { DataSummaryField } from './DataSummaryField'
import { Entry, Form } from 'shared/types'
import { EntryQuickPreviewDrawer } from './EntryQuickPreviewButton'
import { TimeAgoField } from 'shared'
import { parseURLParams } from 'shared/helpers'
import { ACCOUNT_FORMS_SUBSCRIPTION } from 'shared/gql'

import EntryPagination from './Pagination'
import { Link } from 'react-router-dom'
import { useSubscription } from '@apollo/client'
import AppState from 'shared/app_state';

const entryFilters = [<BooleanInput source="spam" label="Spam" defaultValue={false} />]

const EntriesBulkActionButtons = (props) => (
  <>
    <BulkUpdateButton {...props} label="Mark as Spam" data={{ spam: true }} icon={null} mutationMode="pessimistic" />
    <BulkUpdateButton {...props} label="Unmark as Spam" data={{ spam: false }} icon={null} mutationMode="pessimistic" />
    <BulkDeleteButton {...props} />
  </>
)

const ListActions = ({ onExport, ...props }) => (
  <TopToolbar>
    <FilterButton />
    <Button label="Export" onClick={onExport}>
      <GetAppIcon />
    </Button>
  </TopToolbar>
)

const TagsField: React.FC = () => {
  const record = useRecordContext()
  if (!record) return null
  if (record.spam) return <Tag color="red">SPAM</Tag>
  return null
}

export const EntryList = () => {
  const record = useRecordContext() // record is a form
  const dataProvider = useDataProvider()
  const [panelEntry, setPanelEntry] = useState<string>(null)

  const onExport = async () => {
    try {
      const { data: entries }: { data: Entry[] } = await dataProvider.getList('Entry', {
        pagination: { page: 1, perPage: 1000 }, // This is the default react-admin export limit
        sort: { field: 'createdAt', order: 'ASC' },
        filter: { formId: record.id },
      })

      const entriesForExport = entries.map((e: Entry) => {
        // remove nested object id attr that ra-data-graphql-simple provider inserts
        // https://github.com/marmelab/react-admin/blob/master/packages/ra-data-graphql-simple/src/getResponseParser.js#L27
        if (e['form.id']) delete e['form.id']
        if (e['form']) delete e['form']
        if (e['spamDetectionType']) delete e['spamDetectionType']
        if (e['spamFingerprint']) delete e['spamFingerprint']
        if (e['spamEvaluation']) {
          const spamEvaluation = e['spamEvaluation']
          delete e['spamEvaluation']
          if (!!spamEvaluation.score) e['spamScore'] = spamEvaluation.score
        }
        if (e['internalMeta']) delete e['internalMeta']
        if (e['humanReadableInternalMeta']) {
          e['meta'] = e['humanReadableInternalMeta']
          delete e['humanReadableInternalMeta']
        }
        return e
      })
      jsonExport(entriesForExport, {}, (err, csv) => {
        downloadCSV(csv, `formtopia-${record.name.toLowerCase()}-entries-${new Date().toLocaleDateString()}.csv`)
      })
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    if (record) {
      const { entry } = parseURLParams([{ key: 'entry' }])
      if (!!entry) setPanelEntry(entry)
    }
  }, [])

  if (!record) return null

  const dataFieldsDisplayedKeys = record.config.filter((input) => !!input.name).map((input) => input.name)

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <List
        actions={<ListActions onExport={onExport} />}
        resource="Entry"
        filter={{ formId: record.id }}
        filters={entryFilters}
        perPage={25}
        pagination={<EntryPagination />}
        exporter={false}
        sort={{ field: 'createdAt', order: 'DESC' }}
        empty={<EmptyEntryListContent />}
      >
        <RealtimeDataGrid rowClick={(id) => setPanelEntry(id as string)} dataDisplayedKeys={dataFieldsDisplayedKeys} />
      </List>
      <EntryQuickPreviewDrawer
        onClose={() => {
          setPanelEntry(null)
        }}
        record={panelEntry}
        showPanel={!!panelEntry}
        displayedKeys={dataFieldsDisplayedKeys}
      />
    </div>
  )
}

const RealtimeDataGrid = ({ rowClick, dataDisplayedKeys }) => {
  const record = useRecordContext() // record is a form
  const { refetch: refetchEntries } = useListContext()

  useSubscription(ACCOUNT_FORMS_SUBSCRIPTION, {
    variables: { accountId: AppState.getCurrentAccount().id },
    onSubscriptionData: (data) => {
      const { subscriptionData: { data: { accountForms } } } = data
      if (accountForms?.payload?.form_id != record.id) return
      if (['form_entry_create', 'form_entry_delete'].includes(accountForms.payload.event)) refetchEntries()
    }
  })

  return (
    <Datagrid rowClick={rowClick} bulkActionButtons={<EntriesBulkActionButtons />}>
      <DataSummaryField source="data" displayedKeys={dataDisplayedKeys} />
      <TimeAgoField source="createdAt" label="Created" />
      <TagsField source="tags" sortable={false} />
    </Datagrid>
  )
}

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { styled } from '@mui/material/styles';

interface PlaceholderProps {
  className?: string;
}

const Placeholder = (props: PlaceholderProps) => (
  <Root className={props.className}>&nbsp;</Root>
);

const PREFIX = 'RaPlaceholder';

const Root = styled('span', {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
  display: 'flex',
}));

const EmptyEntryListContent = () => {
  return (
    <div style={{ minWidth: 800, marginTop: 20, marginLeft: 'auto', marginRight: 'auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell align="right">Created</TableCell>
                <TableCell align="right">Tags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="right"><Placeholder /></TableCell>
                <TableCell align="right"><Placeholder /></TableCell>
                <TableCell align="right"><Placeholder /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="right"><Placeholder /></TableCell>
                <TableCell align="right"><Placeholder /></TableCell>
                <TableCell align="right"><Placeholder /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="right"><Placeholder /></TableCell>
                <TableCell align="right"><Placeholder /></TableCell>
                <TableCell align="right"><Placeholder /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div style={{ textAlign: 'center' }}><Typography.Title level={3}>You're on your way!</Typography.Title></div>
      <div style={{ textAlign: 'center' }}><Typography.Title level={4}>This form hasn't collected any entries yet.</Typography.Title></div>
      <div style={{ textAlign: 'center' }}>
        <Steps labelPlacement="vertical">
          <Steps.Step status={'process'} disabled title={<Link to={'design'}>DESIGN</Link>} description="Edit your form fields." />
          <Steps.Step status={'process'} disabled title={<Link to={'publish'}>PUBLISH</Link>} description="Link or embed your form." />
          <Steps.Step status={'process'} disabled title={<Link to={'workflow'}>WORKFLOW</Link>} description="Set up Alerts and Integrations." />
        </Steps>
      </div>
    </div>
  )
}
